import { Link } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

import routes from "routes";
import footerRoutes from "footer.routes";

import bgImage from "assets/images/bg-about-us.jpg";

const reasons = [
  {
    icon: "school",
    title: "Strong Academic Program",
    description: "Our comprehensive curriculum is designed to challenge and inspire students at every level. We follow a rigorous academic program that prepares students for secondary education and beyond, with a focus on critical thinking, problem-solving, and creative expression.",
  },
  {
    icon: "record_voice_over",
    title: "English-Speaking Learning Environment",
    description: "All instruction is conducted in English, ensuring students develop strong communication skills essential for success in today's global world. Our English-medium approach helps students become confident speakers, readers, and writers.",
  },
  {
    icon: "people",
    title: "Dedicated, Qualified Teachers",
    description: "Our teaching staff consists of qualified, experienced, and passionate educators who are committed to bringing out the best in every child. They receive ongoing professional development to stay current with best teaching practices.",
  },
  {
    icon: "security",
    title: "Safe & Child-Friendly Campus",
    description: "Safety is our top priority. Our secure campus features controlled access, constant supervision, and safety protocols. The environment is designed to be welcoming and comfortable for children of all ages.",
  },
  {
    icon: "apartment",
    title: "Modern Learning Spaces",
    description: "Our classrooms are bright, well-ventilated, and equipped with modern teaching aids. We provide an environment that stimulates learning and creativity, with dedicated spaces for different activities and subjects.",
  },
  {
    icon: "computer",
    title: "Modern Computer Laboratory",
    description: "Students gain essential digital literacy skills in our well-equipped computer lab. From basic computer operations to educational software, we prepare students for the technology-driven future.",
  },
  {
    icon: "psychology",
    title: "Holistic Child Development",
    description: "We focus on developing the whole child—intellectually, physically, socially, emotionally, and spiritually. Through various programs and activities, we ensure balanced growth in all areas of development.",
  },
  {
    icon: "gavel",
    title: "Strong Discipline & Moral Values",
    description: "We maintain high standards of discipline while nurturing moral development. Our students learn self-control, respect, responsibility, and integrity—values that will serve them throughout life.",
  },
  {
    icon: "church",
    title: "Christian Environment",
    description: "Our Christ-centered approach integrates faith into daily learning. Students participate in devotions, chapel services, and character education grounded in biblical principles, fostering spiritual growth alongside academic achievement.",
  },
];

function WhyChooseUs() {
  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/admissions",
          label: "Enroll Now",
          color: "success",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="75vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              Why Choose Us?
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.8} mt={1} mb={3}>
              Discover what makes Rose of Sharon Primary School the ideal choice for your child&apos;s education
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={6}>
          <Container>
            <Grid container justifyContent="center" sx={{ textAlign: "center", mb: 6 }}>
              <Grid item xs={12} md={8}>
                <MKTypography variant="h3" mb={2}>
                  Why Choose Rose of Sharon Primary School?
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  We are committed to providing an exceptional educational experience that prepares 
                  your child for success. Here are the key reasons why parents trust us with their 
                  children&apos;s education.
                </MKTypography>
              </Grid>
            </Grid>
            <Grid container spacing={4}>
              {reasons.map((reason, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <MKBox
                    p={3}
                    borderRadius="xl"
                    sx={{
                      backgroundColor: "#f8f9fa",
                      height: "100%",
                      transition: "all 0.3s",
                      "&:hover": {
                        backgroundColor: "#e8f5e9",
                        transform: "translateY(-5px)",
                        boxShadow: ({ boxShadows: { lg } }) => lg,
                      },
                    }}
                  >
                    <MKBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      borderRadius="50%"
                      sx={{ backgroundColor: "#006747", mb: 2 }}
                    >
                      <Icon sx={{ color: "#d32f2f", fontSize: "1.5rem" }}>{reason.icon}</Icon>
                    </MKBox>
                    <MKTypography variant="h5" mb={1}>
                      {reason.title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {reason.description}
                    </MKTypography>
                  </MKBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        {/* Call to Action */}
        <MKBox
          component="section"
          py={6}
          sx={{
            background: "linear-gradient(135deg, #006747 0%, #004d35 100%)",
            borderRadius: "xl",
            mx: { xs: 1, lg: 3 },
            my: 4,
            textAlign: "center",
          }}
        >
          <Container>
            <MKTypography variant="h3" color="white" mb={2}>
              Ready to Join Our Family?
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.9} mb={4}>
              Take the first step towards giving your child an exceptional education.
            </MKTypography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <MKButton
                  variant="contained"
                  color="white"
                  size="large"
                  component={Link}
                  to="/admissions"
                  sx={{ color: "#006747" }}
                >
                  Apply Now
                </MKButton>
              </Grid>
              <Grid item>
                <MKButton
                  variant="outlined"
                  color="white"
                  size="large"
                  component={Link}
                  to="/contact"
                >
                  Contact Us
                </MKButton>
              </Grid>
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default WhyChooseUs;
